import { useMemo } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { InfoCard } from '@backstage/core-components';
import {
  makeStyles,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@material-ui/core';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  parseCostHistory,
  allCostTypes,
  totalForMonth,
  formatUsd,
} from './parseCostHistory';

const PALETTE = [
  '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
  '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
  '#aec7e8', '#ffbb78', '#98df8a', '#ff9896', '#c5b0d5',
];

const useStyles = makeStyles(theme => ({
  chartContainer: {
    width: '100%',
    height: 400,
    marginBottom: theme.spacing(4),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  totalCell: {
    fontWeight: 700,
  },
  headerCell: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
}));

function formatMonthLabel(month: string): string {
  const d = new Date(`${month}-01`);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
}

export const CostHistoryContent = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const data = parseCostHistory(entity.metadata.annotations as Record<string, string>);

  const { chartData, months, costTypes } = useMemo(() => {
    if (!data) return { chartData: [], months: [], costTypes: [] };

    const m = Object.keys(data).sort();
    const types = allCostTypes(data);

    const cd = m.map(month => {
      const entry: Record<string, string | number> = { month: formatMonthLabel(month) };
      for (const t of types) {
        entry[t] = data[month][t] ?? 0;
      }
      entry._total = totalForMonth(data[month]);
      return entry;
    });

    return { chartData: cd, months: m, costTypes: types };
  }, [data]);

  if (!data || months.length === 0) {
    return (
      <Box className={classes.empty}>
        <Typography variant="h6" color="textSecondary">
          No cost history available for this resource.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <InfoCard title="Cost History — Breakdown by Usage Type">
        <Box className={classes.chartContainer}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis
                tickFormatter={(v: number) => `$${v.toLocaleString()}`}
              />
              <Tooltip
                formatter={(value: number, name: string) => [formatUsd(value), name]}
                labelFormatter={(label: string) => label}
              />
              <Legend />
              {costTypes.map((type, i) => (
                <Area
                  key={type}
                  type="monotone"
                  dataKey={type}
                  stackId="1"
                  stroke={PALETTE[i % PALETTE.length]}
                  fill={PALETTE[i % PALETTE.length]}
                  fillOpacity={0.6}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </InfoCard>

      <InfoCard title="Monthly Cost Breakdown">
        <TableContainer component={Paper} variant="outlined" className={classes.tableContainer}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>Month</TableCell>
                {costTypes.map(t => (
                  <TableCell key={t} align="right" className={classes.headerCell}>
                    {t}
                  </TableCell>
                ))}
                <TableCell align="right" className={classes.headerCell}>
                  Total
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {months.map(month => (
                <TableRow key={month}>
                  <TableCell>{formatMonthLabel(month)}</TableCell>
                  {costTypes.map(t => (
                    <TableCell key={t} align="right">
                      {data[month][t] != null
                        ? formatUsd(data[month][t])
                        : '—'}
                    </TableCell>
                  ))}
                  <TableCell align="right" className={classes.totalCell}>
                    {formatUsd(totalForMonth(data[month]))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </InfoCard>
    </>
  );
};
